import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import keycloak from '../../hooks/keycloak';
import Loader from '../../components/Loader/Loader';
import {
    storeData,
    triggerNotification,
    useGetApiRequests
} from '../../common/CommonExport';
import { setOrgId } from '../../redux/slices/orgSlice';
import { setToken } from '../../redux/slices/authSlice';
import { setUserData } from '../../redux/slices/userDataSlics';
import { setRolePermissions } from '../../redux/slices/rolePermissionsSlice';
import { setUserId } from '../../redux/slices/userIdSlice';
import { setOrgName } from '../../redux/slices/OrganisationNameSlice';
import { constMessage } from '../../constants/constantsMessage';
import { setEmployeePermission, setHrLogin, setIsAdmin, setSwitchIcon } from '../../redux/slices/employeePermissionSlice';
import { setEmployeeDetails } from '../../redux/slices/employee/employeeDetailsSlice';
import { handleRequestError } from '../../layouts/toast/ErrorNotificationMessage';
import { useIsMobile } from '../../hooks/commonHooks'; // Keep hooks if needed for logic reuse


const KeycloakAuthHandler = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const socialLoginAPI = useGetApiRequests('socialLogin', 'POST');
    const loginUserDetails = useGetApiRequests('userLoginDetails', 'GET');
    const loginEmployeeDetails = useGetApiRequests('getEmployeeDetails', 'GET');
    const isMobile = useIsMobile();

    // Prevent double calls in Strict Mode
    const hasCalledAPI = useRef(false);
    const [loading, setLoading] = useState(keycloak.authenticated);

    const handleGetEmployeeDetails = async (employeeId: any) => {
        try {
            const response: any = await loginUserDetails('', {}, { employeeId });
            if (response?.data?.status?.success === 'Success') {
                dispatch(setRolePermissions(response?.data?.data?.role?.rolePermissions));
                dispatch(setUserData(response.data));
                dispatch(setOrgName(response.data.data?.role?.organisation?.name));
                storeData('userId', response?.data?.data?.userId, true);
                storeData('loginUserData', response?.data?.data, true);
                sessionStorage.setItem("hrDeptId", response?.data?.data?.role?.organisation?.hrDeptId);
                storeData('isLoggedIn', true, true);
                navigate('/home/dashboard');
                dispatch(setSwitchIcon(true));
                setLoading(false);
            }
        } catch (err: any) {
            triggerNotification('error', '', err?.response?.data?.status?.message ?? err?.message, 'topRight');
        }
    };

    const configureHRandReportingManager = (employeeId: string | number, hrLogin: boolean) => {
        dispatch(setHrLogin(hrLogin));
        handleGetEmployeeDetails(employeeId);
        dispatch(setEmployeePermission(false));
    };

    const getEmployeeDetails = async (employeeId: number | string, isEmployee: boolean = false) => {
        try {
            const response: any = await loginEmployeeDetails('', {}, { employeeId });
            const data = await response.data;
            if (data?.status === 200) {
                dispatch(setEmployeePermission(isEmployee));
                dispatch(setEmployeeDetails(data?.data));
                if (isEmployee) {
                    dispatch(setSwitchIcon(false));
                    navigate('/user-dashboard');
                } else {
                    dispatch(setSwitchIcon(true));
                    navigate('/home/dashboard');
                }
                setLoading(false);
            }
        } catch (err: any) {
            triggerNotification('error', '', err?.response?.data?.status?.message ?? err?.message, 'topRight');
        }
    };

    const handleKeycloakLogin = async (retryCount = 0) => {
        if (!keycloak.authenticated || !keycloak.token) return;

        // If it's the first call (retryCount === 0), enforce single execution check.
        // If it's a retry (retryCount > 0), allow it even if hasCalledAPI is true.
        if (retryCount === 0 && hasCalledAPI.current) return;

        if (retryCount === 0) {
            hasCalledAPI.current = true; // Mark as called on first valid attempt
            setLoading(true);
        }

        const ssoIssuer = process.env.REACT_APP_AKKU_SSO_ISSUER;

        const payload = {
            email: keycloak.tokenParsed?.email,
            subjectToken: keycloak.token,
            subjectIssuer: ssoIssuer,
            googleUserId: keycloak.subject // 'sub' claim from token
        };

        try {
            const response: any = await socialLoginAPI(payload);
            const responseMessage = response?.data?.message;

            if (response.status === 200) {
                if (responseMessage === constMessage.finderCloakUser) {
                    if (retryCount < 20) {
                        setTimeout(() => {
                            handleKeycloakLogin(retryCount + 1);
                        }, 500);
                    } else {
                        console.error("KeycloakAuthHandler: Max retries reached for finderCloakUser.");
                        triggerNotification('error', '', 'Login failed after multiple attempts. Please contact support.', 'topRight');
                        setLoading(false);
                    }
                } else {
                    const employeeId = JSON.parse(response?.data?.data?.employeeId)[0];
                    storeData('loggedInEmployeeId', employeeId, true);
                    storeData('authToken', response?.data?.data?.accessToken, true);

                    dispatch(setOrgId(JSON.parse(response?.data?.data?.organisationId)[0]));
                    dispatch(setUserId(employeeId));
                    dispatch(setToken(response?.data?.data?.accessToken));

                    const { roles } = response?.data?.data;

                    if (roles && roles.some((role: any) => role.name === "admin")) {
                        dispatch(setIsAdmin(true));
                        if (roles?.length === 1) {
                            if (isMobile) {
                                navigate('/home/dashboard');
                                setLoading(false);
                            } else {
                                configureHRandReportingManager(employeeId, true);
                            }
                        } else {
                            if (isMobile) {
                                getEmployeeDetails(employeeId);
                                handleGetEmployeeDetails(employeeId);
                            } else {
                                configureHRandReportingManager(employeeId, false);
                            }
                        }
                    } else {
                        dispatch(setIsAdmin(false));
                        getEmployeeDetails(employeeId, true);
                    }
                }
            } else {
                console.error('KeycloakAuthHandler: API returned status', response.status);
                setLoading(false);
            }
        } catch (err: any) {
            console.error('KeycloakAuthHandler: Error calling API', err);
            handleRequestError(err);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (keycloak.authenticated) {
            handleKeycloakLogin();
        }
    }, [keycloak.authenticated]);

    return loading ? <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white"><Loader /></div> : null;
};

export default KeycloakAuthHandler;
